import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import ColorPicker from '../../../../components/ColorPicker';
import DuplicateDealMessage from '../../../../components/DuplicateDealMessage';
import { useLEAccordionItemContext } from '../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import VehicleInfo from '../../../../components/VehicleInfo';
import { Input, Label, Select } from '../../../../components/formComponents';
import {
  LICENSE_PLATE_NAME,
  VIN_NAME,
} from '../../../../components/licensePlateOrVinInput/LicensePlateOrVinInput';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import { COUNTRY_CONFIG } from '../../../../constants/countries';
import { statesArray } from '../../../../constants/states';
import {
  Country,
  KbbVinResult,
  Maybe,
  useTemporaryInfoUpdateMutation,
} from '../../../../gql/generated/graphql';
import { CookieKeys, useCookie } from '../../../../hooks/useCookie';
import useDecodeCar from '../../../../hooks/useDecodeCar';
import { stateValidation } from '../../../../utils/validation/address';
import validateLicenseNumber, {
  INVALID_LICENSE_PLATE_MSG,
  INVALID_VIN_MSG,
} from '../../../../utils/validation/licenseNumberValidator';
import validateVin from '../../../../utils/validation/vinValidator';

type CarInfo = {
  license_plate_number: string;
  license_plate_state: string;
  vin: string;
  good_through_date: Date;
  make: string;
  model: string;
  color: string;
  kbb_trim_name: string;
  fuel_type: string;
  vehicle_type: string;
};

const carInfoInitialValues: CarInfo = {
  license_plate_number: '',
  license_plate_state: '',
  vin: '',
  good_through_date: new Date(new Date().setDate(new Date().getDate() + 7)),
  make: '',
  model: '',
  color: '',
  kbb_trim_name: '',
  fuel_type: '',
  vehicle_type: '',
};

const AffiliateSinglePageCarDetails = ({
  showNextAccordion,
  currentInputType,
  setCurrentInputType,
  setLicensePlateState,
  setMake,
  country,
}: {
  showNextAccordion: () => void;
  currentInputType: string;
  setCurrentInputType: (type: string) => void;
  setLicensePlateState: (state: string) => void;
  setMake: (make: string) => void;
  country: Country;
}) => {
  const { updateState, index, setOpenAccordionIndex } = useLEAccordionItemContext();

  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);
  const isLicensePlate = LICENSE_PLATE_NAME === currentInputType;

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const {
    fetchedCarDetails,
    setFetchedCarDetails,
    kbbOptions,
    setKbbOptions,
    decodingLoading,
    dealExists,
    flowStartError,
    vinData,
    handleDecodeCar,
  } = useDecodeCar();

  const handleSubmit = async (values: CarInfo) => {
    const vehicle: Maybe<KbbVinResult> = vinData?.kbbVin?.vinResults?.find(
      (vinResult) => vinResult?.trimName === values.kbb_trim_name,
    );

    await updateTemporaryInfo({
      variables: {
        info: {
          id: guid,
          data: {
            moved_states: false,
            kbb_vehicle_id: vehicle?.vehicleId,
            kbb_vehicle_name: vehicle?.vehicleName,
            kbb_trim_name: vehicle?.trimName,
            kbb_all_options: vehicle?.vehicleOptions,
            color: values.color,
          },
        },
      },
    });

    setLicensePlateState(values.license_plate_state);
    setMake(values.make);

    showNextAccordion();
    setTimeout(() => {
      updateState({ status: 'complete' });
      setOpenAccordionIndex(index + 1);
    }, 0);
  };

  return (
    <Formik
      initialValues={carInfoInitialValues}
      validationSchema={Yup.object({
        ...(isLicensePlate
          ? {
              license_plate_number: Yup.string()
                .required('Please enter a value')
                .test('is-valid-license-number', INVALID_LICENSE_PLATE_MSG, async (value) =>
                  validateLicenseNumber({ licenseNumber: value }),
                ),
              license_plate_state: stateValidation,
            }
          : {
              vin: Yup.string()
                .required('Please enter a value')
                .test('is-valid-vin', INVALID_VIN_MSG, (value) => validateVin(value)),
            }),
        make: Yup.string().required('Please enter a value'),
        model: Yup.string().required('Please enter a value'),
        color: Yup.string().required('Color is required'),
        kbb_trim_name: Yup.string().required('Please select a trim'),
      })}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      onReset={() => {
        setFetchedCarDetails(undefined);
        setKbbOptions([]);
      }}
    >
      {({ isSubmitting, resetForm, touched, errors, values, setFieldError, setValues }) => (
        <Form>
          <Label>VEHICLE INFORMATION</Label>
          {!fetchedCarDetails && (
            <Flex flexDir="column">
              <Flex w="full" gap={1} flexDir={{ base: 'column', md: 'row' }}>
                {isLicensePlate ? (
                  <Flex w={{ md: '70%' }} gap={1}>
                    <Input
                      _container={{ w: '70%' }}
                      name="license_plate_number"
                      placeholder="License Plate"
                      onChange={() => setFetchedCarDetails(undefined)}
                      invalid={
                        dealExists ||
                        !!flowStartError ||
                        (touched.license_plate_number && !!errors.license_plate_number)
                      }
                    />
                    <Select
                      _container={{ w: '30%' }}
                      name="license_plate_state"
                      placeholder="State"
                      options={statesArray}
                      onChange={() => setFetchedCarDetails(undefined)}
                      invalid={
                        dealExists || (touched.license_plate_state && !!errors.license_plate_state)
                      }
                    />
                  </Flex>
                ) : (
                  <Input
                    _container={{ w: { md: '70%' } }}
                    onChange={() => setFetchedCarDetails(undefined)}
                    name="vin"
                    placeholder="Your vehicle's 17-digit VIN"
                    invalid={dealExists || !!flowStartError || (touched.vin && !!errors.vin)}
                  />
                )}
                <PrimaryButton
                  onClick={async () =>
                    handleDecodeCar({ values, setFieldError, setValues }, isLicensePlate, () =>
                      updateState({ status: 'none' }),
                    )
                  }
                  mt={{ base: 2, md: 0 }}
                  size="sm"
                  h="35px"
                  variant="secondary"
                  fontSize="sm"
                  w={{ base: '50%', md: '30%' }}
                >
                  FIND MY CAR
                </PrimaryButton>
              </Flex>
              {COUNTRY_CONFIG[country].plateLookup && (
                <Text
                  mt={2}
                  onClick={() => {
                    setCurrentInputType(isLicensePlate ? VIN_NAME : LICENSE_PLATE_NAME);
                    resetForm();
                  }}
                  cursor="pointer"
                  fontSize={14}
                  fontWeight={600}
                  color="oceanBoatBlue"
                  textAlign="left"
                  maxW="fit-content"
                >
                  {isLicensePlate ? 'Use VIN' : 'Use License Plate'}
                </Text>
              )}
              {dealExists && (
                <Box mt={1}>
                  <DuplicateDealMessage textAlign="left" />
                </Box>
              )}
            </Flex>
          )}
          {(fetchedCarDetails || decodingLoading) && (
            <>
              <Flex gap={4} mt={2} mb={2} flexDir={{ base: 'column', md: 'row' }}>
                <VehicleInfo
                  year={fetchedCarDetails?.year ?? ''}
                  make={fetchedCarDetails?.make ?? ''}
                  model={fetchedCarDetails?.model ?? ''}
                  vin={fetchedCarDetails?.vin ?? ''}
                  infoLoading={decodingLoading}
                  p={2}
                  w={{ md: '60%' }}
                  hideVin
                />
                <Text
                  display={{ base: 'block', md: 'none' }}
                  onClick={() => resetForm()}
                  cursor="pointer"
                  fontSize={14}
                  fontWeight={600}
                  color="oceanBoatBlue"
                  textAlign="left"
                  maxW="fit-content"
                >
                  This is not my car
                </Text>
                <Box w={{ md: '40%' }} mb="2px" mt="auto">
                  <Select
                    name="kbb_trim_name"
                    placeholder="Select Trim"
                    alignSelf="self-start"
                    w="full"
                    mb={4}
                    options={kbbOptions}
                    isDisabled={kbbOptions.length <= 1}
                  />
                  <ColorPicker hideLabel />
                </Box>
              </Flex>
              <Text
                display={{ base: 'none', md: 'block' }}
                mb={4}
                onClick={() => resetForm()}
                cursor="pointer"
                fontSize={14}
                fontWeight={600}
                color="oceanBoatBlue"
                textAlign="left"
                maxW="fit-content"
              >
                This is not my car
              </Text>
            </>
          )}

          <VStack>
            <PrimaryButton mt="30px" type="submit" isLoading={isSubmitting}>
              NEXT
            </PrimaryButton>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

export default AffiliateSinglePageCarDetails;
